import { LanguageCode } from 'shared/library/i18n/languages';
import { SitemapType } from './sitemap';

export type LangMap = { en: string } & Partial<Record<LanguageCode, string>>;

export type RouteParamValue = string | LangMap;
export type RouteQueryValue = string | number | null | undefined;
export type RouteTabKey = 'index' | 'listings' | 'sell' | 'inbox' | 'me/index';

type ExtractParams<T extends string> = T extends `${string}[${infer Param}]${infer Rest}`
  ? Param | ExtractParams<Rest>
  : never;

type ExtractRouteParams<T extends LangMap> = {
  [K in keyof T]: T[K] extends string ? ExtractParams<T[K]> : never;
}[keyof T];

export type RouteBuildParams<T extends LangMap> =
  ExtractRouteParams<T> extends never
    ? undefined | Record<string, never>
    : Record<ExtractRouteParams<T>, RouteParamValue>;

export type InternalRouteConfig<T extends LangMap> = {
  path: string;
  currentPath?: string | null;
  buildQuery?: (
    params: RouteBuildParams<T>,
    locale: LanguageCode,
  ) => Record<string, RouteQueryValue>;
};

export type InternalRouteDefinition<T extends LangMap> = string | InternalRouteConfig<T>;

export function resolveRoutePath<T extends LangMap>(routes: T, locale: LanguageCode): string {
  return routes[locale] ?? routes.en;
}

function resolveLocalizedParamValue(value: LangMap, locale: LanguageCode): string {
  return value[locale] ?? value.en ?? value.sv ?? Object.values(value)[0] ?? '';
}

export const defineRoute = <N extends string, const Routes extends LangMap>(config: {
  name: N;
  internalRoute?: InternalRouteDefinition<Routes>;
  // The app opens these pages on the website instead of rendering them natively.
  webOnly?: boolean;
  routes: Routes;
  sitemap?: SitemapType;
  preserveCurrentTab?: boolean;
}) => ({
  ...config,
  build: (params: RouteBuildParams<Routes>, locale: LanguageCode = 'en'): string => {
    let path = resolveRoutePath(config.routes, locale);

    if (params) {
      for (const [key, value] of Object.entries(params)) {
        const paramValue =
          typeof value === 'object' && value !== null
            ? resolveLocalizedParamValue(value as LangMap, locale)
            : value;
        path = path.replace(`[${key}]`, (paramValue as string) || '');
      }
    }

    return path;
  },
});
